import { mfaErrorMessage } from '@/utils/mfa-errors';
import {
  browserSupportsWebAuthn,
  WebAuthnError,
} from '@simplewebauthn/browser';

/** The longest passkey name the API accepts. */
export const PASSKEY_LABEL_MAX_LENGTH = 64;

/** True when this browser can create and use passkeys. */
export function passkeysSupported(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/**
 * A name for a new passkey from the browser's user agent, such as
 * "Chrome on Mac". The person can change it before saving.
 */
export function defaultPasskeyLabel(userAgent = ''): string {
  const os = [
    [/iPhone/, 'iPhone'],
    [/iPad/, 'iPad'],
    [/Android/, 'Android'],
    [/CrOS/, 'Chromebook'],
    [/Mac OS X|Macintosh/, 'Mac'],
    [/Windows/, 'Windows'],
    [/Linux/, 'Linux'],
  ].find(([pattern]) => (pattern as RegExp).test(userAgent))?.[1];

  const browser = [
    [/Edg\//, 'Edge'],
    [/Firefox\/|FxiOS/, 'Firefox'],
    [/Chrome\/|CriOS/, 'Chrome'],
    [/Safari\//, 'Safari'],
  ].find(([pattern]) => (pattern as RegExp).test(userAgent))?.[1];

  if (os && browser) {
    return `${browser} on ${os}`;
  }

  return (os as string) ?? 'Passkey';
}

/**
 * A message for a failed passkey step. The browser's own errors (the
 * person closed the prompt, or the device already has a passkey for this
 * account) get plain words; API errors go through mfaErrorMessage.
 */
export function passkeyErrorMessage(err: unknown): string {
  // A WebAuthnError has a code, except for the browser errors it passes
  // through, which keep their DOMException name.
  let name = err instanceof Error ? err.name : undefined;
  if (
    err instanceof WebAuthnError &&
    err.code !== 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY'
  ) {
    name = err.code;
  }

  switch (name) {
    case 'ERROR_CEREMONY_ABORTED':
    case 'NotAllowedError':
    case 'AbortError':
      return 'The passkey prompt was closed or timed out. Try again when you are ready.';
    case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
    case 'InvalidStateError':
      return 'This device already has a passkey for your account. Use another device or security key.';
    case 'ERROR_INVALID_DOMAIN':
    case 'ERROR_INVALID_RP_ID':
    case 'SecurityError':
      return 'Passkeys do not work on this address. Contact support.';
    default:
      return mfaErrorMessage(err);
  }
}
